import {
  configGeneralParameters,
  getBase64FromUrlWithCompression,
} from '../pdfGenerator/pdfGenerator'
import { checkEmpty } from '../utils'
import { CustomFields, TestProps } from '@/@types/global'
import { ReportItems } from '@/@types/report/report-items'
import { ContentProps } from '@/@types/template'
import { getFile, getTemplate } from '@/api/get'

const countImageQuadrant = (sel: any, first: boolean) => {
  const array = first
    ? [
        sel.ampliacao_50x,
        sel.ampliacao_100x,
        sel.ampliacao_200x,
        sel.ampliacao_400x,
        sel.ampliacao_500x,
        sel.ampliacao_1000x,
        sel.externa_50x,
        sel.meio_50x,
        sel.interno_50x,
        sel.externa_100x,
        sel.meio_100x,
        sel.interno_100x,
        sel.externo_200x,
        sel.meio_200x,
        sel.interna_200x,
        sel.externa_400x,
        sel.meio_400x,
        sel.interno_400x,
        sel.externa_500x,
        sel.meio_500x,
        sel.interno_500x,
        sel.externa_1000x,
        sel.meio_1000x,
        sel.interno_1000x,
      ]
    : [
        sel.ampliacao_50x_2,
        sel.ampliacao_100x_2,
        sel.ampliacao_200x_2,
        sel.ampliacao_400x_2,
        sel.ampliacao_500x_2,
        sel.ampliacao_1000x_2,
        sel.externa_50x_2,
        sel.meio_50x_2,
        sel.interno_50x_2,
        sel.externa_100x_2,
        sel.meio_100x_2,
        sel.interno_100x_2,
        sel.externo_200x_2,
        sel.meio_200x_2,
        sel.interno_200x_2,
        sel.externa_400x_2,
        sel.meio_400x_2,
        sel.interno_400x_2,
        sel.externa_500x_2,
        sel.meio_500x_2,
        sel.interno_500x_2,
        sel.externa_1000x_2,
        sel.meio_1000x_2,
        sel.interno_1000x_2,
      ]

  return array.filter((value) => value != null).length
}

export const getParameters = async (
  selected: ReportItems[],
  customFields: CustomFields,
  test: TestProps
): Promise<ContentProps> => {
  const folding = await getTemplate({ id: 'microstructure_photos' })
  const params = folding.generalContent.content

  configGeneralParameters(params, selected, customFields, test)

  let selIdx = 0
  let countQuadrant1Before = 0
  let countQuadrant2Before = 0

  // eslint-disable-next-line no-restricted-syntax
  for (const [index, sel] of selected.entries()) {
    await toFillIn(sel)

    const nextSelected =
      selected.length > index + 1 ? selected[index + 1] : undefined
    const countQuadrant1 = countImageQuadrant(sel, true)
    const countQuadrant2 = countImageQuadrant(sel, false)

    const countQuadrant1NextSelected = nextSelected
      ? countImageQuadrant(nextSelected, true)
      : 0
    const countQuadrant2NextSelected = nextSelected
      ? countImageQuadrant(nextSelected, false)
      : 0

    const addPageBreak =
      countQuadrant1Before +
        countQuadrant2Before +
        countQuadrant1 +
        countQuadrant2 +
        countQuadrant1NextSelected +
        countQuadrant2NextSelected >
      6

    selIdx = gen(params, selIdx, sel, true, addPageBreak)

    if (sel.micro_estrutura_codmice2 || countQuadrant2 > 0) {
      const addPageBreak2 =
        countQuadrant1Before +
          countQuadrant2Before +
          countQuadrant1 +
          countQuadrant2 +
          countQuadrant1NextSelected +
          countQuadrant2NextSelected >
        6

      // if (addPageBreak2) {
        selIdx += 1
        selIdx = gen(params, selIdx, sel, false, addPageBreak2)
      // }
    }
    selIdx += 1

    countQuadrant1Before = countQuadrant1
    countQuadrant2Before = countQuadrant2
  }
  return params
}

export const toFillIn = async (sel: ReportItems) => {
  await postAllRelevantFields(sel)
}

const postField = async (
  key: string,
  value: any,
  postFn: (param: any) => Promise<any>
): Promise<PostResult> => {
  const result = await postFn(value)
  return { key, url: result ?? '' }
}

const postAllRelevantFields = async (r: DataObject): Promise<void> => {
  if (!r) return

  const entries = Object.entries(r) as [string, any][]
  const promises: Promise<PostResult>[] = entries
    .filter(([key, value]) => {
      const isTextField = key.endsWith('_path')
      return isTextField && value !== null && value !== undefined
    })
    .map(([key, value]) => postField(key, `attachment/${value}`, getFile))

  try {
    const responses = await Promise.all(promises)
    const results = await Promise.all(responses.map((res) => res))

    const promisesBase64: Promise<PostResult>[] = results.map(({ key, url }) =>
      postField(key, url, getBase64FromUrlWithCompression)
    )

    const resultsBase64 = await Promise.all(promisesBase64)

    resultsBase64.forEach(({ key, url }) => {
      r[key.replace('_path', '')] = url
    })
  } catch (error) {
    console.error('Erro ao enviar campos:', error)
  }
}

const gen = (params, selIdx, sel, firstQuadrant, addPageBreak) => {
  const columnsPt = JSON.parse(JSON.stringify(params.bodyDefinitionDef[0]))
  const columnsEn = JSON.parse(JSON.stringify(params.bodyDefinitionDef[1]))

  columnsPt.forEach((column: any) => {
    if (selIdx !== 0 && addPageBreak) {
      column.pageBreak = 'before'
    }
  })

  if (firstQuadrant) {
    columnsPt[8].text = 'Resultado - Quadrante 1'
    columnsEn[8].text = '(Result - Quadrant 1)'
  } else {
    columnsPt[8].text = 'Resultado - Quadrante 2'
    columnsEn[8].text = '(Result - Quadrant 2)'
  }

  params.bodyDefinition.push(columnsPt)
  params.bodyDefinition.push(columnsEn)

  params.bodyDefinition.push([
    { text: checkEmpty(sel.date_authorised), style: 'smallerCenter' },
    { text: checkEmpty(sel.corrida), style: 'smallerCenter' },
    { text: checkEmpty(sel.ordem_de_processo), style: 'smallerCenter' },
    { text: checkEmpty(sel.lote_tt), style: 'smallerCenter' },
    { text: checkEmpty(sel.ippn), style: 'smallerCenter' },
    { text: checkEmpty(sel.posicao_amostra), style: 'smallerCenter' },
    { text: checkEmpty(sel.id_ensaio), style: 'smallerCenter' },
    { text: checkEmpty(sel.amostra_mes), style: 'smallerCenter' },
    {
      text: checkEmpty(
        firstQuadrant ? sel.micro_estrutura : sel.micro_estrutura_codmice2
      ),
      style: 'smallerCenter',
      colSpan: 3,
    },
    {},
    {},
  ])

  const imagesToAdd: any[] = []
  const ampliacoes = [50, 100, 200, 400, 500, 1000]
  const variacoes = ['ampliacao', 'interno', 'meio', 'externa', 'externo']

  ampliacoes.forEach((x) => {
    variacoes.forEach((tipo) => {
      const key = `${tipo}_${x}x${!firstQuadrant ? '_2' : ''}`
      const textKey = `${tipo}_${x}x${!firstQuadrant ? '_2' : ''}_text`

      if (sel[key]) {
        addImagePathWithTranslate(sel[key], imagesToAdd, sel[textKey])
      }
    })
  })

  const imageCopy = JSON.parse(JSON.stringify(params.images))

  for (let i = 0; i < imagesToAdd.length; i += 3) {
    const image = imagesToAdd[i]
    const nextImage = imagesToAdd[i + 1]
    const nextNextImage = imagesToAdd[i + 2]

    if (
      (selIdx === 0 && i !== 0 && i % 6 === 3) ||
      (selIdx !== 0 && i !== 0 && i % 6 === 0)
    ) {
      image.text.pageBreak = 'before'
    }
    const lineText = [image.text]
    const lineTextTranslate = [image.textTranslate]
    const linePhoto = [image.photo]
    if (nextImage) {
      if (
        (selIdx === 0 && i !== 0 && i % 6 === 3) ||
        (selIdx !== 0 && i !== 0 && i % 6 === 0)
      ) {
        nextImage.text.pageBreak = 'before'
      }
      lineText.push(nextImage.text)
      lineTextTranslate.push(nextImage.textTranslate)
      linePhoto.push(nextImage.photo)
    } else {
      lineText.push({ fillColor: '#DCDCDC', text: ' ' })
      lineTextTranslate.push({ fillColor: '#DCDCDC', text: ' ' })
      linePhoto.push({ fillColor: '#DCDCDC', text: ' ' })
    }
    if (nextNextImage) {
      if (
        (selIdx === 0 && i !== 0 && i % 6 === 3) ||
        (selIdx !== 0 && i !== 0 && i % 6 === 0)
      ) {
        nextNextImage.text.pageBreak = 'before'
      }
      lineText.push(nextNextImage.text)
      lineTextTranslate.push(nextNextImage.textTranslate)
      linePhoto.push(nextNextImage.photo)
    } else {
      lineText.push({ fillColor: '#DCDCDC', text: ' ' })
      lineTextTranslate.push({ fillColor: '#DCDCDC', text: ' ' })
      linePhoto.push({ fillColor: '#DCDCDC', text: ' ' })
    }

    imageCopy.table.body.push(lineText)
    imageCopy.table.body.push(lineTextTranslate)
    imageCopy.table.body.push(linePhoto)
  }
  params.bodyDefinition.push([
    imagesToAdd.length > 0 ? imageCopy : {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
  ])

  return selIdx
}

const TITLE_PHOTO = {
  fillColor: '#DCDCDC',
  style: 'boldSmallerCenter',
  text: '',
}
const PHOTO = {
  fillColor: '#FFFFFF',
  width: 200,
  image: '',
  style: 'center',
  height: 150,
}

const addImagePathWithTranslate = (imageItem, imagesToAdd, textDescription) => {
  addImagepath(imageItem, imagesToAdd, textDescription, true)
}

const addImagepath = (
  imagePathItem,
  imagesToAdd,
  textDescription,
  useTranslate
) => {
  if (imagePathItem) {
    const itemText = JSON.parse(JSON.stringify(TITLE_PHOTO))
    itemText.text = textDescription

    const itemPhoto = JSON.parse(JSON.stringify(PHOTO))
    itemPhoto.image = imagePathItem

    const itemTextTranslate = JSON.parse(JSON.stringify(TITLE_PHOTO))

    if (useTranslate && textDescription) {
      const textArray = textDescription.split('(')

      itemText.text = textArray[0]
      if (textArray.length > 1) {
        itemTextTranslate.text = `(${textArray[1]}`
      }
    }

    imagesToAdd.push({
      text: itemText,
      photo: itemPhoto,
      textTranslate: itemTextTranslate,
    })
  }
}
